import axios from 'axios';
import type { AxiosInstance, AxiosResponse } from 'axios';
import { message } from 'antd';
import type { z } from 'zod';

/**
 * axios 实例 — 复刻 bak/frontend/src/api/client.ts 的 {code,message,data} 解包思路。
 *
 * 调用方拿到的不是 AxiosResponse，而是 data 本体。再喂给 zod schema 校验
 * （见 lib/zod-resolver.ts），保证类型 + 运行时双层安全。
 *
 * `_silent` 配置项（M3-ρ-4 R-med#2 教训）：
 *   - 拦截器默认 message.error 全局弹错（兜底）
 *   - 业务侧 mutation/queries 想自定义错误展示（如 truncateError 截断）时传 `{_silent: true}`
 *     → 拦截器只 reject Promise，不弹 toast，避免双 toast
 *   - 仅用于"调用方有 onError 自己处理"的场景；裸调慎用否则失败无任何反馈
 */
declare module 'axios' {
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  export interface AxiosRequestConfig {
    /** 拦截器不弹全局 toast（让调用方 onError 自己 message.error 截断展示） */
    _silent?: boolean;
  }
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: '/api/v1',
  timeout: 30_000,
});

apiClient.interceptors.request.use((config) => {
  if (!(config.data instanceof FormData)) {
    config.headers.set?.('Content-Type', 'application/json');
  }
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.set?.('Authorization', `Bearer ${token}`);
  }
  // Sprint15:不再注入 X-Tenant-Code header — 严格分工后租户上下文走 JWT.tid 单源,
  // 后端零消费 X-Tenant-Code(grep 实证),保留只会让"视角切换"产生隐蔽的跨租户读写。
  // localStorage.tenant_code 仅保留供 TenantThemeProvider 选白标主题用,不参与 API 决策。
  return config;
});

apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // blob/二进制 → 原样返回（下载场景）
    if (response.config.responseType === 'blob') {
      return response;
    }

    const body = response.data as { code?: number; message?: string; data?: unknown };

    // 不带 code 字段（旧接口 / 第三方）— 透传
    if (body == null || body.code === undefined || body.code === null) {
      return response;
    }

    if (body.code !== 0) {
      // _silent：调用方自己接错（典型场景：ρ-4 download/delete mutation 走 truncateError）
      if (!response.config?._silent) {
        message.error(body.message || '请求失败');
      }
      return Promise.reject(new Error(body.message || `code=${body.code}`));
    }

    // 把 data 塞回 response.data，调用方更直观（不用每次 response.data.data）
    response.data = body.data;
    return response;
  },
  (error) => {
    const status = error.response?.status;
    const silent = error.config?._silent === true;
    if (status === 401) {
      localStorage.removeItem('token');
      // 避免无限跳转：登录页本身的 401 不再跳
      if (!window.location.pathname.startsWith('/login')) {
        // 401 强制兜底 toast — 即便 _silent 也要让用户知道为何被踢登录页
        message.error('登录已过期，请重新登录');
        window.location.assign('/login');
      } else if (!silent) {
        // Sprint 12-B-a 修:登录页内 401(密码错/账号锁)必须 toast,
        // 否则用户点登录后毫无反馈(原 bug:此分支只清 token,不弹)
        const msg =
          error.response?.data?.detail ||
          error.response?.data?.message ||
          '用户名或密码错误';
        message.error(msg);
      }
    } else if (status === 429) {
      // 429 单独弹(rate_limit / 锁定 / 通用限流),即便在登录页也展示
      if (!silent) {
        const msg =
          error.response?.data?.detail ||
          error.response?.data?.message ||
          '请求过于频繁,请稍后再试';
        message.error(msg);
      }
    } else if (!silent) {
      const msg =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        '网络错误';
      message.error(msg);
    }
    return Promise.reject(error);
  },
);

/**
 * 简单 GET — 返回解包后的 data 本体。
 *
 * @deprecated 新代码请走 {@link apiGetParsed}(GET + zod runtime parse)。
 *   裸 `apiGet<T>` 无运行时校验,Sprint 7b-hotfix 已暴露 envelope 漂移导致 prod
 *   白屏的真案例(见 docs/dev/lessons-ongoing.md L11-44)。**禁用**形式见
 *   `.eslintrc.cjs` 的 no-restricted-syntax 规则与 paginationSchema 替代写法。
 */
export async function apiGet<T = unknown>(url: string, params?: Record<string, unknown>): Promise<T> {
  const res = await apiClient.get<T>(url, { params });
  return res.data;
}

/**
 * @deprecated 新代码请走 {@link apiPostParsed}(POST + zod runtime parse)。
 */
export async function apiPost<T = unknown>(url: string, body?: unknown): Promise<T> {
  const res = await apiClient.post<T>(url, body);
  return res.data;
}

/**
 * @deprecated 新代码请走 {@link apiPatchParsed}(PATCH + zod runtime parse)。
 */
export async function apiPatch<T = unknown>(url: string, body?: unknown): Promise<T> {
  const res = await apiClient.patch<T>(url, body);
  return res.data;
}

export async function apiDelete<T = unknown>(url: string): Promise<T> {
  const res = await apiClient.delete<T>(url);
  return res.data;
}

// ===========================================================================
// zod runtime parse helpers — Sprint 8 contract-drift root cause fix
// ===========================================================================
//
// 历史教训(Sprint 7b-hotfix ★★★ — docs/dev/lessons-ongoing.md L11-44):
//   Architect grep 拿到了 endpoint path 但**没拿 response shape**,假设 list
//   返 array,实际返 `{items,total,page,page_size}` envelope。axios interceptor
//   已剥外层 `{code,message,data}`,内层 `data` 是对象不是数组 → prod 白屏:
//     "(U.data ?? []).map is not a function"
//   vitest 全绿(mock 与 prod shape 不一致),tsc 不报(any cast 通过)。
//
// 机制:fetcher 内 runtime parse,任何 shape 漂移立即:
//   - DEV: throw + console.error(让 bug 在开发期暴露)
//   - PROD: console.warn + toast + reject(可观测 + 用户感知)
//
// 用法:
//   import { apiGetParsed } from '@/api/client';
//   import { paginationSchema } from '@/api/_types/common';
//   const data = await apiGetParsed('/material-types', paginationSchema(materialTypeSchema));
//
// 已存在的 parseOrWarn(`@/lib/zod-resolver`) 是 "fetch then parse" 拆两步;
// 此处 apiGetParsed 是 "fetch + parse 一行" 便利方法(同样底层语义),
// 鼓励新代码统一走这条路径,老代码保留 parseOrWarn 不强迁。
// ===========================================================================

/**
 * zod 解析失败时的统一处理:dev throw / prod warn+toast+reject。
 *
 * **调用约定(Sprint 9 Reviewer M1 修)**:
 *   - **DEV**:throw + console.error → 调用方**禁止**用 `try {...} catch { return [] }` 兜底
 *     吃掉异常(那会让契约破在开发期也悄无声息复发)。若需 fallback,改走
 *     {@link apiGetParsedSafe},语义清晰只在 prod 兜空。
 *   - **PROD**:throw + toast + console.warn → 调用方 catch 后 fallback 是 OK 的
 *     (用户友好降级,空 list 优于白屏)。
 *
 * 已被踩过的反模式(Sprint 8 Reviewer M1):
 *   ```
 *   try { return (await apiGetParsed(...)).items } catch { return [] }
 *   ```
 *   ↑ DEV 也吞 — bug 不可见。改用 {@link apiGetParsedSafe}({} → [],仅 PROD 兜)。
 */
function _handleZodFailure(err: z.ZodError, where: string, raw: unknown): never {
  const issues = err.issues
    .map((i) => `  · ${i.path.join('.') || '<root>'}: ${i.message}`)
    .join('\n');
  const msg = `[contract-drift] ${where}:\n${issues}`;
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.error(msg, { raw });
  } else {
    // eslint-disable-next-line no-console
    console.warn(msg, { raw });
    // QA Sprint 8 Sec L1 修(LOW):toast 文案不含 where(可能含 UUID),仅 console 留 endpoint 详情
    message.error('数据格式错误,请联系运维');
  }
  throw new Error(msg);
}

/** GET + zod parse:契约漂移立即失败(防 Sprint 7b-hotfix 白屏复发)。 */
export async function apiGetParsed<T>(
  url: string,
  schema: z.ZodType<T>,
  params?: Record<string, unknown>,
): Promise<T> {
  const raw = await apiGet<unknown>(url, params);
  const result = schema.safeParse(raw);
  if (!result.success) {
    _handleZodFailure(result.error, `GET ${url}`, raw);
  }
  return result.data;
}

/** POST + zod parse:写操作出参也强校验(同上语义)。 */
export async function apiPostParsed<T>(
  url: string,
  schema: z.ZodType<T>,
  body?: unknown,
): Promise<T> {
  const raw = await apiPost<unknown>(url, body);
  const result = schema.safeParse(raw);
  if (!result.success) {
    _handleZodFailure(result.error, `POST ${url}`, raw);
  }
  return result.data;
}

/** PATCH + zod parse。 */
export async function apiPatchParsed<T>(
  url: string,
  schema: z.ZodType<T>,
  body?: unknown,
): Promise<T> {
  const raw = await apiPatch<unknown>(url, body);
  const result = schema.safeParse(raw);
  if (!result.success) {
    _handleZodFailure(result.error, `PATCH ${url}`, raw);
  }
  return result.data;
}

/**
 * Sprint 9 Reviewer M1 修 — DEV throw / PROD fallback 双语义 helper。
 *
 * 解决双层 catch 问题:`apiGetParsed` DEV throw 的设计被 call site 的
 * `try { ... } catch { return [] }` 吃掉,DEV 也跟 PROD 一样静默 — 完全失效。
 *
 * 此 helper 的契约:
 *   - **DEV**:zod parse 失败 → throw(开发者必须看到、修 schema 或 fixture)
 *   - **PROD**:zod parse 失败 → 返 fallback(用户友好降级,console.warn 已在
 *     `_handleZodFailure` 里 + toast 已弹)
 *
 * 用法(替代旧 try/catch return [] pattern):
 *   ```ts
 *   // before(双层 catch — DEV 也吞):
 *   try { return (await apiGetParsed(url, schema)).items; } catch { return []; }
 *
 *   // after(单层语义清晰):
 *   const resp = await apiGetParsedSafe(url, schema, { items: [] } as never);
 *   return resp.items;
 *   ```
 */
export async function apiGetParsedSafe<T>(
  url: string,
  schema: z.ZodType<T>,
  fallback: T,
  params?: Record<string, unknown>,
): Promise<T> {
  try {
    return await apiGetParsed(url, schema, params);
  } catch (err) {
    if (import.meta.env.DEV) {
      // DEV:throw 让开发者立即看到契约破裂,禁止吞
      throw err;
    }
    // PROD:fallback,避免用户白屏(_handleZodFailure 已 toast + warn 日志)
    return fallback;
  }
}
